import { useRef, useState } from "react";
import styled from "styled-components";
import {
  ActionMenu,
  CircularLoader,
  Icon,
  Layout,
  Option,
  OptionList,
  Popover,
  Tag,
  Title,
  useTheme
} from "@introist/react-foundation/v2";

import { AvatarBackground } from "modules/settings/components/AvatarBackground";

import { TUserRole, WorkspaceUser } from "services/rpc/RpcProvider";

import { capitalizeFirstLetter } from "utils/Text";
import { List, ListRow } from "components/organisms";

import { describeScopes, MemberScope } from "./scopes";

type UserListProps = {
  users: WorkspaceUser[];
  loading: boolean;
  readOnly?: boolean;
  canManageAccess?: boolean;
  scopesByUser: Record<string, MemberScope[]>;
  onRemoveUser: (userId: string) => Promise<unknown>;
  onChangeRole: (userId: string, role: TUserRole) => unknown;
  onManageAccess: (userId: string) => void;
};

const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const RolePopoverButton = styled(Layout.Group)<{ $readOnly?: boolean }>`
  border: none;
  background-color: transparent;
  padding: 0;
  cursor: ${({ $readOnly }) => ($readOnly ? "default" : "pointer")};
`;

const ROLE_SELECT_OPTIONS: Option[] = [
  {
    key: "admin" as TUserRole,
    title: "Admin"
  },
  {
    key: "owner" as TUserRole,
    title: "Owner"
  },
  {
    key: "user" as TUserRole,
    title: "User"
  }
];

const AccessSummary = ({ user, scopes }: { user: WorkspaceUser; scopes: MemberScope[] }) => {
  const { theme } = useTheme();
  const text = user.role === "admin" || user.role === "owner" ? "Full access" : describeScopes(scopes);

  return (
    <Title small color={theme.palette.foreground.subdued}>
      {text}
    </Title>
  );
};

const RoleSelect = ({
  user,
  readOnly,
  onChange
}: {
  user: WorkspaceUser;
  onChange: (role: TUserRole) => void;
  readOnly?: boolean;
}) => {
  const { theme } = useTheme();

  const rolePopoverAnchor = useRef<HTMLDivElement | null>(null);
  const [rolePopoverOpen, setRolePopoverOpen] = useState(false);

  return (
    <RolePopoverButton
      gap="small"
      ref={rolePopoverAnchor}
      onClick={() => {
        !readOnly && setRolePopoverOpen(!rolePopoverOpen);
      }}
      $readOnly={readOnly}
    >
      <Title>{capitalizeFirstLetter(user.role)}</Title>
      {!readOnly && <Icon name="chevronDown" color={theme.palette.foreground.subdued} />}

      <Popover
        placement="bottom-end"
        open={rolePopoverOpen}
        referenceElement={rolePopoverAnchor.current}
        onClose={() => setRolePopoverOpen(false)}
        style={{ width: 172 }}
      >
        <OptionList
          options={ROLE_SELECT_OPTIONS}
          selected={user.role}
          onItemClick={nextSelected => onChange(nextSelected.key as TUserRole)}
        />
      </Popover>
    </RolePopoverButton>
  );
};

export const UserList = ({
  users,
  loading,
  readOnly,
  canManageAccess,
  scopesByUser,
  onRemoveUser,
  onChangeRole,
  onManageAccess,
  ...rest
}: UserListProps) => {
  const { theme } = useTheme();

  if (loading) {
    return (
      <Empty>
        <CircularLoader fillParent />
      </Empty>
    );
  }

  return (
    <List {...rest}>
      {users.map((user, index) => (
        <ListRow key={`workspace-member-list-item-${index}`} gridTemplateColumns="2fr 1fr 1fr">
          <Layout.Group>
            <AvatarBackground $subdued={!user.active}>
              <Title variant="bold">{user.email[0].toUpperCase()}</Title>
            </AvatarBackground>
            <Layout.Group vertical gap="xSmall">
              <Title>{user.email}</Title>
              <AccessSummary user={user} scopes={scopesByUser[user.userId] ?? []} />
            </Layout.Group>
          </Layout.Group>
          <Layout.Group justifyContent="flex-start">
            <RoleSelect
              user={user}
              onChange={role => onChangeRole(user.userId, role)}
              readOnly={readOnly}
            />
          </Layout.Group>
          <Layout.Group justifyContent="flex-end" gap="xLarge">
            {!user.active && <Tag>Pending</Tag>}
            {(canManageAccess || !readOnly) && (
              <ActionMenu
                attachToRef={false}
                options={[
                  ...(canManageAccess
                    ? [
                        {
                          key: "Manage access",
                          startAdornment: <Icon name="key" />,
                          onClick: () => onManageAccess(user.userId)
                        }
                      ]
                    : []),
                  ...(!readOnly
                    ? [
                        {
                          key: "Remove user",
                          startAdornment: (
                            <Icon name="trash" color={theme.palette.danger.default} />
                          ),
                          titleStyles: {
                            color: theme.palette.danger.default
                          },
                          onClick: () => onRemoveUser(user.userId)
                        }
                      ]
                    : [])
                ]}
              />
            )}
          </Layout.Group>
        </ListRow>
      ))}
    </List>
  );
};
